/**
 * The brand-kit header card and folder decoration for the image picker.
 *
 * TWO SURFACES, ONE MODULE, because they are two halves of one idea: the
 * folder screen says which kits exist, and the header card says what the kit
 * you opened contains and offers to apply the whole thing. Splitting them
 * would put the palette-swatch vocabulary in two files.
 *
 * WHY THIS IS NOT A SECOND SEAM. `mountCollectionList` already calls
 * `onRender` after every render pass, and the picker already occupies it with
 * `paintChrome()`. Both functions here are called FROM that one hook rather
 * than adding a second — the kit rule is that `onRender` is a seam, not a home,
 * and a surface gets one.
 *
 * WHY EVERYTHING REBUILDS. `renderList()` wipes `listEl` on every pass, so the
 * folder decoration cannot be "placed once": it lives inside the rebuilt
 * subtree and is re-created each paint. The header card is the opposite case —
 * it is a sibling of `listEl` inside the scroller, so it SURVIVES a list
 * rebuild and is therefore reconciled by key rather than blindly re-created,
 * which is what keeps focus inside its Apply button when the grid repaints
 * underneath it.
 *
 * THE APPLY BUTTON'S ORDERING IS LOAD-BEARING, and it is the one thing in this
 * file that is not cosmetic. `applyBrandKit()` opens with a preflight that
 * closes anything which would swallow its history entry, and it commits inside
 * one `store.beginTransaction`. Running it while the picker is still open means
 * committing underneath a modal with a caller awaiting a pick. So the button
 * SETTLES THE PICKER FIRST — resolving the pending pick as cancelled, which is
 * true: the user did not choose an image — and only then applies. `onApply` is
 * injected rather than imported so this module has no static edge to the apply
 * engine, and so a test can drive the ordering without a store.
 */

import { createButton } from '../../ui/components.js';
import { LOGONURI_ORIGIN } from '../../templates/logonuri-api.js';

/** Marker class on the header, used to find and reconcile it across paints. */
const HEAD_CLASS = 'pb-kit-head';
/** Which kit the mounted header is for, so a kit switch is detectable. */
const HEAD_SLUG_ATTR = 'data-kit-slug';

/** Human labels for the parts a kit apply fills. Order is the apply's order. */
const PART_LABELS = Object.freeze({
  logo: 'logo',
  palette: 'palette',
  favicon: 'favicon',
  ogImage: 'social card'
});

/**
 * Which parts THIS kit can actually fill.
 *
 * Derived from the kit's own asset map, never assumed: a kit published without
 * a social card must not have a header promising to fill one, because the
 * promise is the whole reason someone presses the button.
 */
export function fillablePartsOf(kit) {
  const assets = kit?.assets || {};
  const parts = [];
  if (assets.logo || assets.logo_transparent) parts.push('logo');
  if ((kit?.palette || []).length) parts.push('palette');
  if (assets.logo_transparent || assets.favicon_32x32) parts.push('favicon');
  if (assets.og_blank) parts.push('ogImage');
  return parts;
}

/**
 * The sentence under the Apply button.
 *
 * In a NARROWED picker it leads with what the user came for and names the rest
 * as an extra, because that is the honest framing: they are here to fill one
 * slot and this is an offer, not the task. In the general picker it is a plain
 * statement of what the button does. Both end in the same place — a list the
 * user can check against the confirm dialog they are about to see.
 */
export function fillSentence(parts, purposePart) {
  const named = parts.map((part) => PART_LABELS[part]).filter(Boolean);
  if (!named.length) return '';
  if (purposePart && parts.includes(purposePart)) {
    const rest = parts.filter((part) => part !== purposePart).map((part) => PART_LABELS[part]);
    if (!rest.length) return `Fills the ${PART_LABELS[purposePart]}.`;
    return `Also fills ${listSentence(rest)} — one step, one undo.`;
  }
  return `Fills ${listSentence(named)} — one step, one undo.`;
}

function listSentence(words) {
  if (words.length <= 1) return words[0] || '';
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** The purpose→part map, so a narrowed picker knows what it came for. */
const PURPOSE_PART = Object.freeze({
  og: 'ogImage',
  logo: 'logo',
  'app-icon': 'favicon'
});

/** Short, locale-aware, and empty rather than "Invalid Date" on a bad value. */
export function formatKitDate(value) {
  if (!value) return '';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function textEl(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text != null) el.textContent = String(text);
  return el;
}

/**
 * Paint (or remove) the header card for the open kit.
 *
 * @param {object} ctx
 * @param {HTMLElement} ctx.scroller  the collection scroller
 * @param {HTMLElement} ctx.listEl    the grid the header sits above
 * @param {object|null} ctx.kit       normalized kit, or null to remove
 * @param {string} [ctx.purpose]      the picker's narrowing purpose, if any
 * @param {(kit:object)=>void} ctx.onApply
 * @returns {HTMLElement|null} the header element, when one is mounted
 */
export function paintKitHeader(ctx) {
  const { scroller, listEl, kit, purpose = '', onApply } = ctx || {};
  if (!scroller) return null;
  const existing = scroller.querySelector(`:scope > .${HEAD_CLASS}`);

  if (!kit?.slug) {
    existing?.remove();
    return null;
  }
  // Reconciled by key, not rebuilt: a blind re-create on every render would
  // move focus out of the Apply button whenever the grid repainted.
  if (existing?.getAttribute(HEAD_SLUG_ATTR) === kit.slug) return existing;
  existing?.remove();

  const head = document.createElement('div');
  head.className = HEAD_CLASS;
  head.setAttribute(HEAD_SLUG_ATTR, kit.slug);
  head.dataset.testid = 'brand-kit-head';

  const purposePart = PURPOSE_PART[purpose] || '';
  if (purposePart) head.classList.add('is-narrowed');

  // ── mark ────────────────────────────────────────────────────────────────
  const markBox = document.createElement('div');
  // The checkerboard is the shared utility, never a local gradient — and it is
  // the thing that makes a transparent mark legible rather than decoration.
  markBox.className = 'pb-kit-head-mark pb-checker';
  const markSrc = kit.markThumb || '';
  if (markSrc) {
    const img = document.createElement('img');
    img.src = markSrc;
    img.alt = '';
    markBox.appendChild(img);
  }
  head.appendChild(markBox);

  // ── name, facts, palette ────────────────────────────────────────────────
  const body = textEl('div', 'pb-kit-head-body');
  body.appendChild(textEl('div', 'pb-kit-head-name', kit.brandName || kit.slug));

  const facts = textEl('div', 'pb-kit-head-facts');
  // `assetCount` is the raw LISTING's count when the view supplies it — the
  // normalized map keeps only placeable keys, and a header that says "6
  // assets" above seven cards and a spinning video is counting the wrong set.
  const assetCount = Number.isInteger(kit.assetCount)
    ? kit.assetCount
    : Object.keys(kit.assets || {}).length;
  const bits = [`${assetCount} ${assetCount === 1 ? 'asset' : 'assets'}`];
  if (Number.isInteger(kit.version)) bits.push(`version ${kit.version}`);
  const when = formatKitDate(kit.updatedAt);
  if (when) bits.push(`updated ${when}`);
  bits.forEach((bit, i) => {
    if (i) facts.appendChild(textEl('span', 'sep', '·'));
    facts.appendChild(textEl('span', null, bit));
  });
  body.appendChild(facts);

  const swatches = paletteRow(kit);
  if (swatches) body.appendChild(swatches);
  head.appendChild(body);

  // ── the action ──────────────────────────────────────────────────────────
  const actions = textEl('div', 'pb-kit-head-actions');
  const player = turntablePreview(kit);
  if (player) actions.appendChild(player);
  const parts = fillablePartsOf(kit);
  const applyBtn = createButton({
    variant: 'primary',
    label: 'Apply this kit to the project…',
    onClick: () => onApply?.(kit)
  });
  applyBtn.dataset.testid = 'brand-kit-apply';
  // A kit that can fill nothing gets a disabled button with the reason beside
  // it, never a hidden one: the card is already saying "here is your kit", and
  // silently omitting the action reads as the feature being broken.
  if (!parts.length) {
    applyBtn.disabled = true;
    applyBtn.setAttribute('aria-disabled', 'true');
  }
  actions.appendChild(applyBtn);

  const fills = textEl(
    'p',
    'pb-kit-fills',
    parts.length
      ? fillSentence(parts, purposePart)
      : 'This kit has no logo, palette, favicon or social card yet.'
  );
  actions.appendChild(fills);
  head.appendChild(actions);

  scroller.insertBefore(head, listEl || scroller.firstChild);
  return head;
}

/**
 * The turntable preview — a PLAYER, never a pick target (LN-10 slice B).
 *
 * It plays straight from LogoNuri's public asset route (CORS `*`, Range served
 * upstream); the PageStitch proxy never sees the request, which is why the
 * turntable has no grid card — the proxy refuses the key, and a card that
 * cannot be picked is an affordance offered where it cannot act. The header is
 * the honest home: it shows the kit HAS a turntable without promising it can
 * be inserted, and the label says which half is true.
 *
 * Muted + `playsinline` are what make autoplay permissible at all; under
 * `prefers-reduced-motion` it does not autoplay and offers controls instead —
 * a spinning logo is exactly the ornament that preference exists to stop.
 */
function turntablePreview(kit) {
  const src = String(kit?.turntable?.url || '');
  if (!src.startsWith(`${LOGONURI_ORIGIN}/`)) return null;
  const frame = textEl('div', 'pb-kit-turntable');
  frame.title = 'Plays from logonuri.com — the video is not part of the project';

  const video = document.createElement('video');
  video.muted = true;
  // The IDL property alone is not enough: a re-parse (or a browser that reads
  // the content attribute at insert time) sees only the attribute.
  video.setAttribute('muted', '');
  video.loop = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.preload = 'metadata';
  let reduced = false;
  try {
    reduced = !!globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    // No matchMedia (odd embedder) — treat as no stated preference.
  }
  if (reduced) video.controls = true;
  else video.autoplay = true;

  const source = document.createElement('source');
  source.src = src;
  const type = String(kit.turntable.contentType || '');
  if (type.startsWith('video/')) source.type = type;
  video.appendChild(source);
  frame.appendChild(video);
  frame.appendChild(textEl('span', 'pb-kit-turntable-label', 'preview'));
  return frame;
}

/**
 * The QR card's meta line — the mode IS the disclosure.
 *
 * A static code is self-contained: the pixels are the whole artifact. A
 * dynamic one resolves through LogoNuri's short-link host, so the pixels
 * survive an export while the DESTINATION stays editable from the kit owner's
 * account — months later, on a site that already shipped. That asymmetry is
 * exactly what a user deciding "put this on the contact section" needs to
 * know, and it is stated on the card rather than discovered by a customer.
 * An unknown mode (an upstream deploy predating the field) makes NO claim.
 */
export function qrCardMeta(qr) {
  if (qr?.mode === 'static') return 'Static — the destination is fixed in the image';
  if (qr?.mode === 'dynamic') return 'Dynamic — repointable in LogoNuri after export';
  return 'PNG · from your LogoNuri kit';
}

/** The QR card's hover title: where a dynamic code actually points. */
export function qrCardTitleAttr(qr) {
  if (qr?.mode === 'dynamic' && /^[A-HJ-NP-Z2-9]{8}$/.test(String(qr?.code || ''))) {
    return `Scans to logonuri.com/q/${qr.code}`;
  }
  return '';
}

/**
 * The palette strip, with hexes.
 *
 * A palette without values is decoration; with them it is the set of colours
 * about to be written into the project's theme, which is what the user is
 * actually deciding about. Capped at six — beyond that the row wraps into a
 * second line and stops reading as a palette.
 */
function paletteRow(kit) {
  const palette = (kit?.palette || []).slice(0, 6);
  if (!palette.length) return null;
  const row = textEl('div', 'pb-kit-swatches');
  for (const entry of palette) {
    const hex = String(entry?.hex || '');
    if (!/^#[0-9a-f]{6}$/i.test(hex)) continue;
    const chip = textEl('span', 'pb-kit-swatch');
    const dot = textEl('i', null);
    // A validated six-digit hex, so this is a colour and not a style injection.
    dot.style.background = hex;
    chip.append(dot, document.createTextNode(hex.toUpperCase()));
    row.appendChild(chip);
  }
  return row.childElementCount ? row : null;
}

/**
 * Decorate the folder rows of the current render with their palette strips.
 *
 * Runs inside the rebuilt subtree by necessity — `renderList()` wipes `listEl`,
 * so this is re-applied on every paint rather than placed once. Idempotent
 * within a paint: a strip is only added where one is absent, so a double call
 * cannot stack two.
 *
 * @param {object} api the collection browser handle
 */
export function decorateKitFolders(api) {
  if (!api?.getRecords) return 0;
  let painted = 0;
  for (const record of api.getRecords()) {
    const folder = record?._folder;
    if (!folder) continue;
    const el = api.itemEl?.(record.id);
    const poster = el?.querySelector('.pb-coll-poster');
    if (!poster || poster.querySelector('.pb-kit-palette')) continue;
    const colors = (folder.palette || []).filter((hex) => /^#[0-9a-f]{6}$/i.test(String(hex)));
    if (!colors.length) continue;
    const strip = textEl('div', 'pb-kit-palette');
    strip.setAttribute('aria-hidden', 'true');
    for (const hex of colors) {
      const band = textEl('span', null);
      band.style.background = hex;
      strip.appendChild(band);
    }
    poster.appendChild(strip);
    painted += 1;
  }
  return painted;
}

/**
 * The facts line for a folder card.
 *
 * Exported and pure so the session can patch `item.meta` with it — a folder's
 * useful facts are its contents, and `metaLineFor` would render an empty string
 * because a folder has no width, height or format.
 */
export function folderMetaLine(folder) {
  if (!folder) return '';
  const count = Number(folder.assetCount || 0);
  const bits = [`${count} ${count === 1 ? 'asset' : 'assets'}`];
  if (Number.isInteger(folder.version)) bits.push(`v${folder.version}`);
  const when = formatKitDate(folder.updatedAt);
  if (when) bits.push(when);
  return bits.join(' · ');
}
